import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fmtTime } from '@/lib/format';
import { hhmm, minutesOfDay, ymdOf } from './time';
import type { AppointmentListItem, ScheduleBlockItem } from './api';

export const STEP = 15;
const ROW = 22; // px por 15 min
const PX_PER_MIN = ROW / STEP;

export interface Range {
  start: number;
  end: number;
}

export interface GridColumn {
  key: string;
  title: string;
  subtitle?: string;
  color?: string;
  date: string;
  barberId?: string;
  events: AppointmentListItem[];
  blocks: ScheduleBlockItem[];
  closed: Range[];
  highlight?: boolean;
}

export const HATCH: CSSProperties = {
  backgroundImage: 'repeating-linear-gradient(135deg, transparent 0 6px, color-mix(in oklab, var(--chrome) 45%, transparent) 6px 7px)',
  backgroundColor: 'color-mix(in oklab, var(--chrome) 10%, transparent)',
};

interface Placed {
  item: AppointmentListItem;
  top: number;
  height: number;
  lane: number;
  lanes: number;
}

/** Distribui eventos sobrepostos em faixas lado a lado. */
function layout(events: AppointmentListItem[], tz: string, startMin: number, endMin: number): Placed[] {
  const rows = events
    .map((item) => {
      const s = Math.max(startMin, minutesOfDay(item.startsAt, tz));
      const e = Math.min(endMin, Math.max(s + STEP, s + item.durationMinutes));
      return { item, s, e };
    })
    .filter((r) => r.e > r.s)
    .sort((a, b) => a.s - b.s || a.e - b.e);
  const out: Placed[] = [];
  let cluster: { r: (typeof rows)[number]; lane: number }[] = [];
  let clusterEnd = -1;
  const flush = () => {
    const lanes = Math.max(1, ...cluster.map((c) => c.lane + 1));
    for (const c of cluster) {
      out.push({ item: c.r.item, top: (c.r.s - startMin) * PX_PER_MIN, height: (c.r.e - c.r.s) * PX_PER_MIN, lane: c.lane, lanes });
    }
    cluster = [];
  };
  const laneEnds: number[] = [];
  for (const r of rows) {
    if (cluster.length && r.s >= clusterEnd) {
      flush();
      laneEnds.length = 0;
    }
    let lane = laneEnds.findIndex((end) => end <= r.s);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = r.e;
    cluster.push({ r, lane });
    clusterEnd = Math.max(clusterEnd, r.e);
  }
  if (cluster.length) flush();
  return out;
}

function blockRange(b: ScheduleBlockItem, date: string, tz: string, startMin: number, endMin: number): Range | null {
  const sDate = ymdOf(b.startsAt, tz);
  const eDate = ymdOf(new Date(new Date(b.endsAt).getTime() - 1), tz);
  if (date < sDate || date > eDate) return null;
  const s = sDate === date ? minutesOfDay(b.startsAt, tz) : 0;
  const e = eDate === date ? minutesOfDay(new Date(new Date(b.endsAt).getTime() - 1), tz) + 1 : 24 * 60;
  const start = Math.max(startMin, s);
  const end = Math.min(endMin, e);
  return end > start ? { start, end } : null;
}

export function TimeGrid({
  columns,
  tz,
  startMin,
  endMin,
  today,
  colorByBarber,
  onSlotClick,
  onEventClick,
  onBlockClick,
  ariaLabel,
}: {
  columns: GridColumn[];
  tz: string;
  startMin: number;
  endMin: number;
  today: string;
  colorByBarber?: boolean;
  onSlotClick?: (col: GridColumn, minutes: number) => void;
  onEventClick: (item: AppointmentListItem) => void;
  onBlockClick?: (block: ScheduleBlockItem) => void;
  ariaLabel: string;
}) {
  const [nowMin, setNowMin] = useState(() => minutesOfDay(new Date(), tz));
  useEffect(() => {
    const t = setInterval(() => setNowMin(minutesOfDay(new Date(), tz)), 30_000);
    return () => clearInterval(t);
  }, [tz]);

  const height = (endMin - startMin) * PX_PER_MIN;
  const hours = useMemo(() => {
    const list: number[] = [];
    for (let m = Math.ceil(startMin / 60) * 60; m < endMin; m += 60) list.push(m);
    return list;
  }, [startMin, endMin]);

  const placed = useMemo(() => columns.map((c) => layout(c.events, tz, startMin, endMin)), [columns, tz, startMin, endMin]);

  return (
    <div role="region" aria-label={ariaLabel} className="overflow-auto rounded-lg border bg-card" style={{ maxHeight: 'calc(100vh - 17rem)', minHeight: '24rem' }}>
      <div className="flex min-w-max">
        <div className="sticky left-0 z-30 w-14 shrink-0 border-r bg-card">
          <div className="sticky top-0 z-30 h-12 border-b bg-card" />
          <div className="relative" style={{ height }}>
            {hours.map((m) => (
              <span key={m} className="tabular absolute right-2 -translate-y-1/2 text-xs text-muted-foreground" style={{ top: (m - startMin) * PX_PER_MIN }}>
                {hhmm(m)}
              </span>
            ))}
          </div>
        </div>

        {columns.map((col, ci) => {
          const showNow = col.date === today && nowMin >= startMin && nowMin <= endMin;
          return (
            <div key={col.key} className={cn('min-w-36 flex-1 border-r last:border-r-0', col.highlight && 'bg-accent/30')}>
              <div className="sticky top-0 z-20 flex h-12 flex-col justify-center border-b bg-card px-2 text-center">
                <span className="flex items-center justify-center gap-1.5 truncate text-sm font-medium">
                  {col.color && <span className="size-2.5 shrink-0 rounded-full" style={{ background: col.color }} aria-hidden />}
                  <span className="truncate">{col.title}</span>
                </span>
                {col.subtitle && <span className="truncate text-xs text-muted-foreground">{col.subtitle}</span>}
              </div>
              <div
                className="relative cursor-cell"
                style={{
                  height,
                  backgroundImage: `repeating-linear-gradient(to bottom, color-mix(in oklab, var(--border) 90%, transparent) 0 1px, transparent 1px ${ROW * 4}px), repeating-linear-gradient(to bottom, color-mix(in oklab, var(--border) 35%, transparent) 0 1px, transparent 1px ${ROW}px)`,
                  backgroundPosition: `0 ${-(startMin % 60) * PX_PER_MIN}px`,
                }}
                onClick={(e) => {
                  if (!onSlotClick || e.target !== e.currentTarget) return;
                  const y = e.clientY - e.currentTarget.getBoundingClientRect().top;
                  const min = startMin + Math.floor(y / ROW) * STEP;
                  onSlotClick(col, min);
                }}
              >
                {col.closed.map((r, i) => (
                  <div key={`c${i}`} className="pointer-events-none absolute inset-x-0" style={{ ...HATCH, top: (r.start - startMin) * PX_PER_MIN, height: (r.end - r.start) * PX_PER_MIN }} aria-hidden />
                ))}
                {col.blocks.map((b) => {
                  const r = blockRange(b, col.date, tz, startMin, endMin);
                  if (!r) return null;
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onBlockClick?.(b);
                      }}
                      className="absolute inset-x-0 z-[5] overflow-hidden border-y border-chrome/60 px-1.5 py-0.5 text-left text-xs text-muted-foreground hover:brightness-95 focus-visible:outline-2 focus-visible:outline-ring"
                      style={{ ...HATCH, backgroundColor: 'color-mix(in oklab, var(--chrome) 22%, var(--card))', top: (r.start - startMin) * PX_PER_MIN, height: (r.end - r.start) * PX_PER_MIN }}
                      aria-label={`Bloqueio: ${b.reason}`}
                    >
                      <span className="inline-flex items-center gap-1 rounded bg-card/80 px-1">
                        <Lock className="size-3" aria-hidden /> {b.reason}
                      </span>
                    </button>
                  );
                })}
                {placed[ci]!.map((p) => {
                  const color = p.item.barberColor;
                  const w = 100 / p.lanes;
                  const short = p.height < ROW * 3;
                  return (
                    <button
                      key={p.item.id}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick(p.item);
                      }}
                      className={cn(
                        'absolute z-10 overflow-hidden rounded-md border-l-4 border-y border-r px-1.5 py-0.5 text-left text-xs shadow-xs transition hover:z-20 hover:shadow-md focus-visible:z-20 focus-visible:outline-2 focus-visible:outline-ring',
                        p.item.status === 'completed' && 'opacity-70',
                        p.item.status === 'requested' && 'border-dashed',
                        p.item.status === 'in_progress' && 'ring-2 ring-primary',
                        p.item.status === 'no_show' && 'border-oxblood/60 text-oxblood-ink',
                      )}
                      style={{
                        top: p.top + 1,
                        height: p.height - 2,
                        left: `calc(${p.lane * w}% + 2px)`,
                        width: `calc(${w}% - 4px)`,
                        borderLeftColor: colorByBarber ? color : 'var(--primary)',
                        background: `color-mix(in oklab, ${colorByBarber ? color : 'var(--primary)'} 14%, var(--card))`,
                      }}
                      aria-label={`${fmtTime(p.item.startsAt, tz)} ${p.item.customerName}, ${p.item.serviceName}`}
                    >
                      <span className="block truncate font-medium">
                        <span className="tabular">{fmtTime(p.item.startsAt, tz)}</span> {p.item.customerName}
                      </span>
                      {!short && <span className="block truncate text-muted-foreground">{p.item.serviceName}</span>}
                    </button>
                  );
                })}
                {showNow && (
                  <div className="pointer-events-none absolute inset-x-0 z-20 flex items-center" style={{ top: (nowMin - startMin) * PX_PER_MIN }} aria-hidden>
                    <span className="-ml-1 size-2 rounded-full bg-brass" />
                    <span className="h-px flex-1 bg-brass" />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
